import React, { useRef, useState } from 'react';
import styled from 'styled-components';

import { ExpenseAttachment } from '@/store/reducers/payout';
import useLanguage from '@/components/common/hooks/useLanguage';
import {
  ATTACHMENT_ACCEPT,
  MAX_FILES_PER_UPLOAD,
  checkFile,
  isImage,
  useAttachmentUrl,
} from '@/utils/attachments';

export const PaperclipIcon: React.FC<{ size?: number }> = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M21 11.5l-8.6 8.6a5.5 5.5 0 01-7.8-7.8l8.6-8.6a3.7 3.7 0 015.2 5.2l-8.6 8.6a1.8 1.8 0 01-2.6-2.6l7.9-7.9"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * Splits a pick into what can go up and why the rest cannot, so the reason is on
 * screen before anything is uploaded.
 */
export const usePickedFiles = () => {
  const [{ payout }] = useLanguage();
  const [error, setError] = useState('');

  const accept = (picked: FileList | null, alreadyChosen = 0) => {
    const files = Array.from(picked || []);
    const problems = files.map(checkFile);
    const ok = files.filter((_, i) => !problems[i]);

    if (problems.includes('type')) setError(payout.attachment_wrong_type);
    else if (problems.includes('size')) setError(payout.attachment_too_big);
    else if (ok.length + alreadyChosen > MAX_FILES_PER_UPLOAD) setError(payout.attachment_too_many);
    else setError('');

    return ok.slice(0, Math.max(0, MAX_FILES_PER_UPLOAD - alreadyChosen));
  };

  return { accept, error };
};

const Thumb: React.FC<{ attachment: ExpenseAttachment, onOpen: () => void }> = ({
  attachment, onOpen,
}) => {
  const { url, failed } = useAttachmentUrl(attachment);
  const showImage = isImage(attachment.mimeType) && url && !failed;

  return (
    <Tile onClick={onOpen} title={attachment.fileName}>
      {showImage ? (
        <Image src={url} alt={attachment.fileName} />
      ) : (
        <FileFace>
          <FileKind>{attachment.mimeType === 'application/pdf' ? 'PDF' : 'FILE'}</FileKind>
          <FileName>{attachment.fileName}</FileName>
        </FileFace>
      )}
    </Tile>
  );
};

interface Props {
  attachments: ExpenseAttachment[];
  onOpen: (index: number) => void;
  onAdd?: (files: File[]) => unknown;
  onRemove?: (attachment: ExpenseAttachment) => unknown;
}

/**
 * The proof on one line: a thumbnail per file, tap to view. Adding is a tile at
 * the end of the same row, so the place to look and the place to add are one.
 */
const Attachments: React.FC<Props> = ({ attachments, onOpen, onAdd, onRemove }) => {
  const [{ payout }] = useLanguage();
  const input = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const { accept, error } = usePickedFiles();

  const pick = async (list: FileList | null) => {
    const files = accept(list);
    if (input.current) input.current.value = '';
    if (!files.length || !onAdd) return;

    setIsUploading(true);
    try {
      await onAdd(files);
    } finally {
      setIsUploading(false);
    }
  };

  const remove = (attachment: ExpenseAttachment) => {
    // eslint-disable-next-line no-alert
    if (onRemove && window.confirm(payout.remove_file_confirm)) onRemove(attachment);
  };

  if (!attachments.length && !onAdd) return null;

  return (
    <Wrapper>
      <Label>{payout.proof}</Label>
      <Tiles>
        {attachments.map((attachment, i) => (
          <TileWrap key={attachment._id}>
            <Thumb attachment={attachment} onOpen={() => onOpen(i)} />
            {onRemove && attachment.canDelete && (
              <Remove aria-label={payout.remove} onClick={() => remove(attachment)}>×</Remove>
            )}
          </TileWrap>
        ))}
        {onAdd && (
          <AddTile disabled={isUploading} onClick={() => input.current && input.current.click()}>
            {isUploading ? (
              <AddText>{payout.uploading}</AddText>
            ) : (
              <>
                <PaperclipIcon size={18} />
                <AddText>{payout.attach}</AddText>
              </>
            )}
          </AddTile>
        )}
      </Tiles>
      {error && <Error>{error}</Error>}
      <input
        ref={input}
        type="file"
        multiple
        accept={ATTACHMENT_ACCEPT}
        style={{ display: 'none' }}
        onChange={(e) => pick(e.target.files)}
      />
    </Wrapper>
  );
};

const Wrapper = styled.div`
  padding: 0 0 12px;
`;

const Label = styled.div`
  text-transform: uppercase;
  font-size: 10px;
  font-weight: bold;
  letter-spacing: 0.5px;
  color: #909599;
  margin-bottom: 6px;
`;

const Tiles = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

const TileWrap = styled.div`
  position: relative;
  margin: 0 8px 8px 0;
`;

const Tile = styled.button`
  display: block;
  width: 64px;
  height: 64px;
  padding: 0;
  border: solid 1px #dae1e8;
  border-radius: 6px;
  background: #f7f8f9;
  overflow: hidden;
  cursor: pointer;
`;

const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const FileFace = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 4px;
`;

const FileKind = styled.div`
  font-size: 11px;
  font-weight: 800;
  color: #d9534f;
  letter-spacing: 0.5px;
`;

const FileName = styled.div`
  font-size: 9px;
  color: #787c80;
  margin-top: 4px;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Remove = styled.button`
  position: absolute;
  top: -6px;
  right: -6px;
  width: 20px;
  height: 20px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: #21272e;
  color: #ffffff;
  font-size: 14px;
  line-height: 20px;
  cursor: pointer;
`;

const AddTile = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  margin: 0 8px 8px 0;
  padding: 0;
  border: dashed 1px #fb7e14;
  border-radius: 6px;
  background: #fff8f1;
  color: #fb7e14;
  cursor: pointer;

  :disabled {
    border-color: #dae1e8;
    background: #f7f8f9;
    color: #909599;
    cursor: default;
  }
`;

const AddText = styled.div`
  font-size: 10px;
  font-weight: bold;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  margin-top: 4px;
`;

const Error = styled.div`
  font-size: 12px;
  color: #d9534f;
  margin-top: 2px;
`;

export default Attachments;
